import { apiUrl } from '@algoan/dashboard/env';
import { Dispatch, AsyncThunk } from '@algoan/dashboard/store/store';
import { authRequest } from './auth-thunks';
import {
  fetchUserStart,
  fetchUserSuccess,
  fetchUsersListSuccess,
  fetchUserFailure,
  updateFavoriteProject,
  updateUserWithNewAttributesSuccess,
} from '@algoan/dashboard/store/slice';
import { accessTokenOrganizationIdSelector, accessTokenUserIdSelector } from '@algoan/dashboard/store/selectors';
import { UserDto, UpdateUserDto, User, PaginatedResources, CreateUserDto } from '@algoan/dashboard/model';
import { convertToUser } from '@algoan/dashboard/converters';

export const getUser =
  (organizationId: string, userId: string): AsyncThunk<User | undefined> =>
  async (dispatch: Dispatch) => {
    try {
      const url = `${apiUrl}/v2/organizations/${organizationId}/users/${userId}`;
      dispatch(fetchUserStart());

      const { data: response } = await dispatch(authRequest<UserDto>('get', url));
      const userInformation = convertToUser(response);
      dispatch(fetchUserSuccess(userInformation));

      return userInformation;
    } catch (error) {
      dispatch(fetchUserFailure({ error: 'genericError' }));
    }
    return undefined;
  };

export const getUsersList =
  (organizationId: string): AsyncThunk<void> =>
  async (dispatch: Dispatch) => {
    try {
      const url = `${apiUrl}/v2/organizations/${organizationId}/users?limit=500`;
      dispatch(fetchUserStart());

      const { data: response } = await dispatch(authRequest<PaginatedResources<UserDto>>('get', url));

      const usersListInformation = response.resources.map((user) => convertToUser(user));
      dispatch(fetchUsersListSuccess(usersListInformation));
    } catch (error) {
      dispatch(fetchUserFailure({ error: 'genericError' }));
    }
  };

export const updateUser =
  (
    organizationId: string | undefined,
    userId: string | undefined,
    updateUserDto: UpdateUserDto,
    cancelUpdateUser?: boolean,
    isUsersTable?: boolean,
  ): AsyncThunk<void> =>
  async (dispatch: Dispatch) => {
    if (cancelUpdateUser) {
      return;
    }

    try {
      const url = `${apiUrl}/v2/organizations/${organizationId}/users/${userId}`;

      if (
        (updateUserDto.firstName === '' || updateUserDto.lastName === '',
        organizationId === undefined || userId === undefined)
      ) {
        throw new Error("firstName or lastName or organizationId or userId doesn't exist");
      }

      await dispatch(authRequest<UserDto>('patch', url, updateUserDto));

      !isUsersTable && dispatch(getUser(organizationId as string, userId as string));
    } catch {
      return Promise.reject();
    }
  };

export const createUser =
  (organizationId: string | undefined, createUserDto: CreateUserDto): AsyncThunk<void> =>
  async (dispatch: Dispatch) => {
    try {
      const url = `${apiUrl}/v2/organizations/${organizationId}/users`;

      if (createUserDto.email === undefined || createUserDto.email?.length < 1 || organizationId === undefined) {
        throw new Error("email or organizationId doesn't exist");
      }

      await dispatch(authRequest<UserDto>('post', url, createUserDto));
    } catch {
      return Promise.reject();
    }
  };

export const resendInvitation =
  (userId: string, organizationId: string): AsyncThunk<void> =>
  async (dispatch: Dispatch) => {
    try {
      const url = `${apiUrl}/v2/organizations/${organizationId}/users/${userId}/resend-invitation`;
      await dispatch(authRequest('patch', url));
    } catch (error) {
      dispatch(fetchUserFailure({ error: 'genericError' }));
    }
  };

export const deleteUser =
  (organizationId: string | undefined, userId: string | undefined): AsyncThunk<void> =>
  async (dispatch: Dispatch) => {
    try {
      const url = `${apiUrl}/v2/organizations/${organizationId}/users/${userId}`;

      if (organizationId === undefined || userId === undefined || userId?.length < 1) {
        throw new Error("userId or organizationId doesn't exist");
      }
      await dispatch(authRequest<UserDto>('delete', url));
    } catch {
      return Promise.reject();
    }
  };

export const patchUserAttributes =
  (newAttributes: {
    favoriteProject?: string;
    shouldBeNotifiedOnAnalysisCreated?: boolean;
    shouldBeNotifiedOnEveryAnalysisCreatedOfOrganization?: boolean;
  }): AsyncThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState();
    const organizationId = accessTokenOrganizationIdSelector(state);
    const userId = accessTokenUserIdSelector(state);
    const URL = `${apiUrl}/v2/organizations/${organizationId}/users/${userId}/attributes`;

    const { data: user } = await dispatch(authRequest<UserDto>('patch', URL, newAttributes));

    if (user.attributes?.favoriteProject) {
      dispatch(updateFavoriteProject(user.attributes.favoriteProject?.[0]));
    }
    dispatch(updateUserWithNewAttributesSuccess(convertToUser(user)));
  };
